import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAlert } from '../../context/AlertContext';
import {
  useGetLegalClientFullObject,
  useDownloadRegisterDocuments,
} from '../../hooks/reactQuerys/LegalClientQueries';
import { useDownloadDocument } from '../../hooks/reactQuerys/DocumentQueries';

interface Props {
  idLegalClient: number;
  open: boolean;
  onClose: () => void;
}

const DOCUMENT_TYPES = {
  AD: { id: 20, label: 'Aktueller Abdruck' },
  SI: { id: 21, label: 'Strukturierter Registerinhalt' },
  GL: { id: 22, label: 'Gesellschafterliste' },
} as const;

export const RegisterDocumentDownloadDialog = ({
  idLegalClient,
  open,
  onClose,
}: Props) => {
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();

  const { legalClientFullObject, isFetchingLegalClientFullObject } =
    useGetLegalClientFullObject(idLegalClient);

  const { mutateAsync: downloadDocs, isPending } =
    useDownloadRegisterDocuments();
  const { downloadDocument, isPending: isDownloadingDocument } =
    useDownloadDocument();

  const [selected, setSelected] = useState<string[]>([]);

  const AVAILABLE_TYPES = Object.keys(
    DOCUMENT_TYPES
  ) as (keyof typeof DOCUMENT_TYPES)[];
  const EXISTING_DOC_TYPES = Object.values(DOCUMENT_TYPES).map(t => t.id);

  const TYPE_LABEL_BY_ID = Object.fromEntries(
    Object.values(DOCUMENT_TYPES).map(t => [t.id, t.label])
  ) as Record<number, string>;

  const toggle = (t: string) =>
    setSelected(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );

  //Dokumente filtern (Typ 20/21/22)
  const existingDocs = useMemo(() => {
    const docs = (legalClientFullObject?.Documents ?? []) as any[];

    return docs.filter(
      d =>
        typeof d.idDocument === 'number' &&
        EXISTING_DOC_TYPES.includes(d.idLegalClientDocumentType)
    );
  }, [legalClientFullObject]);

  //Neue Dokumente beziehen
  const handleDownload = async () => {
    if (selected.length === 0) {
      showAlert({
        text: 'Bitte mindestens einen Dokumenttyp auswählen.',
        severity: 'warning',
      });
      return;
    }

    try {
      const res = await downloadDocs({
        idLegalClient,
        types: selected,
      });
      await queryClient.invalidateQueries({
        queryKey: ['legalclient/fullObject', idLegalClient],
      });
      showAlert({ text: 'Dokument(e) wurden bezogen!' });
    } catch {
      showAlert({
        text: 'Fehler beim beziehen der Dokumente.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        Registerdokumente herunterladen
        {(isFetchingLegalClientFullObject || isPending) && (
          <LinearProgress sx={{ mt: 2 }} />
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* ---------------------------------- */}
        {/* Bereich 1: Dokumente erstellen */}
        {/* ---------------------------------- */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
            Dokumente beziehen
          </Typography>

          {AVAILABLE_TYPES.map(t => (
            <FormControlLabel
              key={t}
              control={
                <Checkbox
                  checked={selected.includes(t)}
                  onChange={() => toggle(t)}
                />
              }
              label={DOCUMENT_TYPES[t].label}
            />
          ))}
        </Paper>

        {/* ---------------------------------- */}
        {/* Bereich 2: Bestehende Dokumente */}
        {/* ---------------------------------- */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
            Bereits vorhandene Registerdokumente
          </Typography>

          {existingDocs.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Keine Registerdokumente vorhanden.
            </Typography>
          ) : (
            <List dense>
              {existingDocs.map(doc => (
                <Box key={doc.idDocument}>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        onClick={() => downloadDocument(doc.idDocument)}
                      >
                        {isDownloadingDocument ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon fontSize='small' />
                        )}
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={doc.FileName}
                      secondary={
                        TYPE_LABEL_BY_ID[doc.idLegalClientDocumentType]
                      }
                    />
                  </ListItem>
                  <Divider />
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>

        <Button
          variant='contained'
          disabled={selected.length === 0 || isPending}
          onClick={handleDownload}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
